/* eslint-disable no-console */
/* ========================================================================== *
 * SENTRY INITIALIZATION                                                      *
 * -------------------------------------------------------------------------- *
 * For bundling size reduction, this file should be imported using a _dynamic *
 * import_: here we will statically import the various functions we need from *
 * Sentry (by statically importing, the bundler will prune all that is not    *
 * needed), and "main.ts" will import this dynamically, so that all of Sentry *
 * sources will _not_ be in the main bundle.                                  *
 * ========================================================================== */

import { watch } from 'vue'

import { user } from '@/init/client'
import { env } from '@/init/env'

import type { captureException } from '@sentry/browser'
import type { App } from 'vue'

let sentryCaptureException: typeof captureException | undefined = undefined

export function handleError(error: any, source: string = 'handleError'): void {
  console.error(`%cERROR %c(source="${source}")%c`, 'color:red', 'color:grey', 'color:inherit', error?.stack || error)

  sentryCaptureException?.(error, {
    tags: { source },
    level: 'error',
  })
}

export function log(...args: any[]): void {
  if (env.DEV) console.log(...args)
}

export function setupSentry(app: App): void {
  /* We have seen sentry **hanging** in production while loading, so we'll
   * load it asynchronously, and only after it's loaded we'll initialize it */
  import('@sentry/browser').then(({ init, setUser, captureException }) => {
    init({
      dsn: 'https://2597032ef673d09c71436ee75c5f4fdf@o4506342696681472.ingest.us.sentry.io/4509366625304576',
      environment: env.MODE,
      release: env.VITE_SENTRY_RELEASE,
      tracesSampleRate: 1.0,
      ignoreErrors: [
        'Network Error', // We don't care
        'Write permission denied', '_AutofillCallbackHandler', // In-app browsers
        /^(?=.*\bAccess token\b)(?=.*\bhas been revoked\b).*$/, // Access token revoked
      ],
    })

    /* Set up error handlers */
    app.config.errorHandler = (err) => handleError(err, 'vue.errorHandler')
    window.addEventListener('error', (event) => handleError(event.error, 'window.addEventListener(error)'))
    window.addEventListener('unhandledrejection', (event) => handleError(event.reason, 'window.addEventListener(unhandledrejection)'))

    /* Expose Sentry functions */
    sentryCaptureException = captureException

    /* Set up user (NOTE: this is an internal app, so OK for GDPR!) */
    watch(user, (user) => setUser(user ? {
      id: user.uuid,
      email: user.email,
    } : null), { immediate: true })

    console.log('Sentry initialized')
  }).catch((error) => {
    console.error('Failed to load Sentry:', error)
  })
}
